class ScHalloweenOffer extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.button = this.querySelector('[data-halloween-copy]');
    this.status = this.querySelector('[data-halloween-status]');
    this.action = this.querySelector('[data-halloween-action]');
    this.copyIcon = this.querySelector('[data-halloween-icon]');
    this.checkIcon = this.querySelector('[data-halloween-check]');
    this.defaultAction = this.action.textContent;
    this.button.addEventListener('click', () => this.activate(), { signal: this.controller.signal });
    document.addEventListener('cart:updated', (event) => this.cartUpdated(event), { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async copyCode() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.dataset.code);
        return true;
      }
    } catch {}
    const text = document.createElement('textarea');
    text.value = this.dataset.code;
    text.setAttribute('readonly', '');
    text.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px';
    document.body.append(text);
    text.select();
    text.setSelectionRange(0, text.value.length);
    let copied = false;
    try { copied = document.execCommand('copy'); } catch {}
    text.remove();
    this.button.focus({ preventScroll: true });
    return copied;
  }

  async applyCode() {
    const root = window.Shopify?.routes?.root || '/';
    const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!currentResponse.ok) throw new Error('cart');
    const current = await currentResponse.json();
    const codes = new Set((current.discount_codes || []).map((entry) => entry.code.toUpperCase()));
    codes.add(this.dataset.code);
    const drawerId = document.querySelector('cart-drawer')?.dataset.sectionId;
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: [...codes].join(','), ...(drawerId ? { sections: drawerId, sections_url: location.pathname } : {}) })
    });
    if (!response.ok) throw new Error('discount');
    const cart = await response.json();
    const accepted = cart.discount_codes?.some((entry) => entry.code.toUpperCase() === this.dataset.code && entry.applicable);
    this.waitingForItem = cart.item_count === 0;
    const result = accepted ? 'active' : this.waitingForItem ? 'saved' : 'unavailable';
    if (result === 'saved') {
      const saved = await fetch(`${root}discount/${encodeURIComponent(this.dataset.code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`, { headers: { Accept: 'application/json' } });
      if (!saved.ok) throw new Error('discount-session');
    }
    const drawer = document.querySelector('cart-drawer');
    if (cart.sections && drawer?.renderCartContents) {
      try { await drawer.renderCartContents({ source: 'sc-halloween', sections: cart.sections, itemCount: cart.item_count }); } catch {}
    }
    return result;
  }

  async activate() {
    if (this.busy) return;
    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');
    this.action.textContent = this.dataset.copying;
    const copyPromise = this.copyCode();
    const applyPromise = this.applyCode().catch(() => 'failed');
    const [copied, result] = await Promise.all([copyPromise, applyPromise]);
    this.copied = copied;
    this.showResult(copied, result);
    this.button.removeAttribute('aria-busy');
    this.busy = false;
  }

  showResult(copied, result) {
    this.dataset.state = result;
    this.copyIcon.toggleAttribute('hidden', copied);
    this.checkIcon.toggleAttribute('hidden', !copied);
    this.action.textContent = copied ? this.dataset.copied : this.defaultAction;
    if (result === 'active' || result === 'saved') {
      this.status.textContent = copied ? this.dataset[result] : this.dataset.applyOnly;
    } else {
      this.status.textContent = copied ? this.dataset.copyOnly : this.dataset.failed;
      if (result === 'unavailable') this.status.textContent = this.dataset.unavailable;
    }
  }

  async cartUpdated(event) {
    if (event.detail?.data?.source === 'sc-halloween' || !this.waitingForItem || this.busy) return;
    this.busy = true;
    try { this.showResult(this.copied, await this.applyCode()); } catch {}
    this.busy = false;
  }
}

if (!customElements.get('sc-halloween-offer')) customElements.define('sc-halloween-offer', ScHalloweenOffer);
